import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { generateNewTabName } from '../../../helper/generateNewTabName'
import {
  hasAttributeValue,
  getPassedPointsName,
  parseAttributes,
  findAttributeValue,
  saveActivePointToStorage
} from '../../../actions/static'
import MessageButton from '../MessageButton'
import { buttonType, attributeName, attributeValue } from '../../../constants/string'
import { SelectDateIcon } from '../../../icons/Icons'
import moment from 'moment'
import { palette } from '../../../theme/palette'

const Button = ({
  onClick,
  button,
  settings,
  setIframe,
  isPanoramaPost,
  setDatePickerSettings,
  dropdownItem,
  isDisabledComponentWithButton,
  isLoadingIframeData,
  selectedRatingItem
}) => {
  const {url, payload, title, type, isWebview} = button

  const postbackButton = type === buttonType.POSTBACK
  const webUrlInWebViewButton = type === buttonType.WEB_URL && isWebview
  const webUrlOutOfWebViewButton = type === buttonType.WEB_URL && !isWebview
  const resetButton = type === buttonType.RESET
  const downloadButton = type === buttonType.DOWNLOAD
  const downloadImageButton = type === buttonType.DOWNLOAD_IMAGE
  const selectDateButton = type === buttonType.SELECT_DATE
  const dropdownButton = type === buttonType.DROPDOWN
  const ratingButton = type === buttonType.RATING

  const storageKey = selectDateButton ? 'passedSelectDatesName' : 'passedQuestionnairesName'
  const passedQuestionnairesName = getPassedPointsName(settings.chatHistoryLevel, storageKey)
  const getUrlMetadata = useMemo (() => url ? new URL(url).searchParams.get('metadata') : '', [url])
  const [isPrimaryButton, setIsPrimaryButton] = useState(false)

  const extraDisablingConditions = () => {
    if (dropdownButton) {
      return !dropdownItem?.option || isDisabledComponentWithButton
    } else if (ratingButton) {
      return !selectedRatingItem || isDisabledComponentWithButton
    } else if (selectDateButton) {
      const attributes = parseAttributes(payload)
      const selectDateUUID = findAttributeValue(attributes, 'selectDateUUID').value
      return passedQuestionnairesName && passedQuestionnairesName.includes(selectDateUUID)
    }
    return false
  }

  const isButtonDisabled = url ? (passedQuestionnairesName && passedQuestionnairesName.includes(getUrlMetadata)) : extraDisablingConditions()

  const getQuestionnaireName = () => {
    const attributePayload = postbackButton ? payload : url
    const preventDisableQuestionnaireButton = hasAttributeValue(attributePayload, attributeName.PREVENT_DISABLE_QUESTIONNAIRE_BUTTON, attributeValue.TRUE)
    return preventDisableQuestionnaireButton ? '' : getUrlMetadata
  }

  const handleOpenWebview = () => {
    if (!isButtonDisabled) {
      setIframe({
        iframeSrc: url,
        isIframeOpen: true,
        questionnaireName: getQuestionnaireName()
      })
    }
  }

  useEffect(() => {
    const attributePayload = postbackButton || dropdownButton || ratingButton || selectDateButton ? payload : url
    const hasPrimaryAttributeValue = hasAttributeValue(attributePayload, attributeName.BUTTON_STYLE_TYPE, attributeValue.PRIMARY)
    setIsPrimaryButton(hasPrimaryAttributeValue)
  }, [url, type])

  const downloadImageHandler = async () => {
    const link = document.createElement('a')

    link.href = url
    link.target = '_self'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const openSelectDate = () => {
    const attributes = parseAttributes(payload)
    const minDate = JSON.parse(findAttributeValue(attributes, 'minDate').value)
    const maxDate = JSON.parse(findAttributeValue(attributes, 'maxDate').value)
    const currenDateValue = JSON.parse(findAttributeValue(attributes, 'currenDateValue').value)
    const dateFormat = findAttributeValue(attributes, 'dateFormat').value
    const calendarView = findAttributeValue(attributes, 'calendarView').value
    const selectDateUUID = findAttributeValue(attributes, 'selectDateUUID').value
    setDatePickerSettings({
      minDate: moment().add(minDate).toDate(),
      maxDate: moment().add(maxDate).toDate(),
      currenDateValue: moment().add(currenDateValue).toDate(),
      dateFormat,
      calendarView,
      isShowSelectDate: true,
      selectDateUUID,
      payload
    })

    saveActivePointToStorage('LOCAL_STORAGE', 'activeSelectDateName', { questionnaireName: selectDateUUID })
  }

  const hasDisablingByType = [
    webUrlInWebViewButton,
    webUrlOutOfWebViewButton,
    downloadButton,
    downloadImageButton,
    selectDateButton,
    dropdownButton,
    ratingButton
  ].some(item => item) ? isButtonDisabled : false

  const targetAttributeValue = webUrlOutOfWebViewButton || downloadButton ? generateNewTabName() : null
  const hrefAttributeValue = webUrlOutOfWebViewButton || downloadButton ? url : null

  const onClickHandler = () => {
    if (postbackButton || dropdownButton || ratingButton) {
      return onClick && onClick()
    } else if (webUrlInWebViewButton) {
      return handleOpenWebview()
    } else if (resetButton) {
      return window.location.reload()
    } else if (downloadImageButton) {
      return downloadImageHandler()
    } else if (selectDateButton) {
      return openSelectDate()
    } else {
      return () => {}
    }
  }

  const ButtonIcon = useMemo(() => {
    if (selectDateButton) {
      const color = hasDisablingByType && palette.mediumGray
      return <SelectDateIcon color={color} />
    }
    return ''
  }, [type, hasDisablingByType])

  return (
    <MessageButton
      data-test='message-button'
      isPrimaryButton={isPrimaryButton}
      isButtonDisabled={hasDisablingByType}
      title={title}
      target={targetAttributeValue}
      href={hrefAttributeValue}
      onClickHandler={onClickHandler}
      icon={ButtonIcon}
      isLoadingIframeData={isLoadingIframeData}
    />
  )
}

export default Button
